/**
 * Persistent Terms Service
 * 
 * Handles session-based persistent user term modifications that survive heavy refreshes:
 * - Add manual terms that always appear
 * - Delete terms by text pattern that never reappear
 * - Modify terms by editing actual claims text
 * 
 * Each modification syncs immediately to backend for persistence.
 */

// Global document ID for session persistence
let globalDocumentId = null;

export class PersistentTermsService {
    constructor(termsStore, claimsStore, backendService) {
        this.termsStore = termsStore;
        this.claimsStore = claimsStore;
        this.backendService = backendService;
        
        // Use global document ID or generate new one
        if (!globalDocumentId) {
            globalDocumentId = this.generateDocumentId();
        }
        this.documentId = globalDocumentId;
    }
    
    /**
     * Generate a session-based document ID
     */
    generateDocumentId() {
        // Use a combination of timestamp and random for uniqueness within session
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 8);
        return `doc_${timestamp}_${random}`;
    }
    
    /**
     * Add manual term that persists through refreshes
     * @param {string} termName - Term to add
     * @param {string} claimsText - Current claims text
     */
    async addManualTerm(termName, claimsText) {
        console.log(`[PersistentTerms DEBUG] addManualTerm called with: "${termName}"`);
        console.log(`[PersistentTerms] Adding manual term: "${termName}"`);
        
        try {
            // Sync to backend immediately (no optimistic updates needed anymore)
            const response = await this.backendService.request('/add-manual-term', {
                method: 'POST',
                body: JSON.stringify({
                    term_text: termName,
                    claims_text: claimsText,
                    document_id: this.documentId
                })
            });
            
            console.log(`[PersistentTerms DEBUG] Add response type:`, response?.constructor?.name);
            console.log(`[PersistentTerms DEBUG] Is Response object:`, response instanceof Response);
            
            // Parse JSON from response
            const result = response ? await response.json() : null;
            console.log(`[PersistentTerms DEBUG] Parsed add result:`, result ? Object.keys(result) : 'null');
            
            // Apply backend result
            console.log(`[PersistentTerms DEBUG] About to apply backend result for manual term`);
            console.log(`[PersistentTerms DEBUG] Result has term_counts:`, !!result.term_counts);
            this.termsStore.applyBackendResult(result);
            console.log(`[PersistentTerms DEBUG] Backend result applied`);
            
            console.log(`✅ Manual term "${termName}" added successfully`);
            return result;
            
        } catch (error) {
            console.error(`[PersistentTerms DEBUG] ❌ Error adding manual term "${termName}":`, error);
            console.error(`[PersistentTerms DEBUG] Error stack:`, error.stack);
            throw error;
        }
    }
    
    /**
     * Delete all instances of a term by text - persists through refreshes
     * @param {string} termText - Term text to delete
     * @param {string} claimsText - Current claims text  
     * @param {number|null} specificClaim - Optional: only delete from specific claim
     */
    async deleteTermByText(termText, claimsText, specificClaim = null) {
        console.log(`[PersistentTerms] Deleting term by text: "${termText}"${specificClaim ? ` (Claim ${specificClaim} only)` : ' (all claims)'}`);
        
        // Get current term counts for fast deletion
        let currentTermCounts = {};
        let unsubscribe = this.termsStore.subscribe(state => {
            // Convert termEntries Map to term_counts object format
            state.termEntries.forEach((termData, indexedKey) => {
                currentTermCounts[indexedKey] = termData;
            });
        });
        unsubscribe(); // Immediately unsubscribe after getting the value
        
        try {
            // Sync to backend immediately with current state for fast update
            const response = await this.backendService.request('/delete-term-pattern', {
                method: 'DELETE',
                body: JSON.stringify({
                    term_text: termText,
                    claims_text: claimsText,
                    claim_num: specificClaim,
                    document_id: this.documentId,
                    current_term_counts: currentTermCounts
                })
            });
            
            console.log(`[PersistentTerms DEBUG] Delete response type:`, response?.constructor?.name);
            console.log(`[PersistentTerms DEBUG] Is Response object:`, response instanceof Response);
            
            // Parse JSON from response
            const result = response ? await response.json() : null;
            console.log(`[PersistentTerms DEBUG] Parsed delete result:`, result ? Object.keys(result) : 'null');
            
            // Apply backend result (all instances should be gone)
            console.log(`[PersistentTerms DEBUG] Applying delete result to store`);
            this.termsStore.applyBackendResult(result);
            console.log(`[PersistentTerms DEBUG] Delete result applied to store`);
            
            console.log(`✅ Term "${termText}" deleted successfully`);
            return result;
            
        } catch (error) {
            console.error(`❌ Error deleting term "${termText}":`, error);
            throw error;
        }
    }
    
    /**
     * Modify term using REPLACE approach: replace text, delete old term, add new term
     * Much simpler than complex position-based modification
     * @param {string} oldTerm - Original term text
     * @param {string} newTerm - New term text
     * @param {string} currentClaimsText - Current claims text content
     * @param {string} _indexedKey - Optional: specific indexed key being modified (unused now)
     */
    async modifyTermInText(oldTerm, newTerm, currentClaimsText, _indexedKey = null) {
        console.log(`[PersistentTerms DEBUG] Starting modifyTermInText`);
        console.log(`[PersistentTerms DEBUG] Input params:`, {
            oldTerm,
            newTerm,
            claimsTextLength: currentClaimsText.length,
            indexedKey: _indexedKey
        });
        console.log(`[PersistentTerms] Modifying term using REPLACE approach: "${oldTerm}" → "${newTerm}"`);
        
        // Set loading state for modify operation
        const operationId = `modify_${oldTerm}_${Date.now()}`;
        this.termsStore.setOperationLoading(operationId, true, { type: 'modify', oldTerm, newTerm });
        
        try {
            // STEP 1: Get current term data to find what needs replacing (parent + child references)
            console.log(`[PersistentTerms] Step 1: Finding term references to replace`);
            
            let termData = null;
            let childTerms = [];
            const unsubscribe = this.termsStore.subscribe(state => {
                // Find the term being modified
                for (const [_key, data] of state.termEntries) {
                    if (data.parent_term.toLowerCase() === oldTerm.toLowerCase()) {
                        termData = data;
                        if (data.children) {
                            childTerms = Object.keys(data.children);
                        }
                        break;
                    }
                }
            });
            unsubscribe();
            
            if (!termData) {
                throw new Error(`Term "${oldTerm}" not found in current terms`);
            }
            
            // STEP 2: Replace only the highlighted occurrences (parent term + child terms)
            console.log(`[PersistentTerms] Step 2: Replacing "${oldTerm}" and ${childTerms.length} child terms with "${newTerm}"`);
            
            let updatedClaimsText = currentClaimsText;
            let totalReplacements = 0;
            
            // Replace parent term occurrences
            const parentRegex = new RegExp(`\\b${oldTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
            const parentMatches = currentClaimsText.match(parentRegex) || [];
            updatedClaimsText = updatedClaimsText.replace(parentRegex, newTerm);
            totalReplacements += parentMatches.length;
            
            // Replace child term occurrences
            for (const childTerm of childTerms) {
                const childRegex = new RegExp(`\\b${childTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
                const childMatches = updatedClaimsText.match(childRegex) || [];
                updatedClaimsText = updatedClaimsText.replace(childRegex, newTerm);
                totalReplacements += childMatches.length;
            }
            
            if (totalReplacements === 0) {
                throw new Error(`No occurrences of "${oldTerm}" or its child terms found in claims text`);
            }
            
            console.log(`[PersistentTerms] Replaced ${totalReplacements} occurrences (parent + children) with "${newTerm}"`);
            
            // Update the claims editor with the new text
            this.updateClaimsEditor(updatedClaimsText);
            
            // STEP 3: Simple rename in UI while backend processes (keeps term visible)
            console.log(`[PersistentTerms] Step 3: Renaming term in UI and marking as updating`);
            
            // Update the term name in the store to keep it visible and mark as updating
            console.log(`[PersistentTerms DEBUG] About to update store with 'updating' status`);
            this.termsStore.update(store => {
                console.log(`[PersistentTerms DEBUG] Current store state before update:`, {
                    termEntriesSize: store.termEntries.size,
                    hasOldTerm: Array.from(store.termEntries.values()).some(d => d.parent_term.toLowerCase() === oldTerm.toLowerCase())
                });
                const newTermEntries = new Map(store.termEntries);
                const newHighlightedEntries = new Set(store.highlightedEntries);
                
                for (const [key, data] of newTermEntries) {
                    if (data.parent_term.toLowerCase() === oldTerm.toLowerCase()) {
                        // Remove from highlighted terms (disable highlighting during update)
                        newHighlightedEntries.delete(key);
                        
                        // Update term with updating status
                        console.log(`[PersistentTerms DEBUG] Setting 'updating' flag for key: ${key}`);
                        newTermEntries.set(key, {
                            ...data,
                            parent_term: newTerm,
                            children: {}, // Clear children as they'll be regenerated
                            user_modified: true,
                            updating: true, // Mark as being updated
                            updating_from: oldTerm // Keep track of what we're updating from
                        });
                        console.log(`[PersistentTerms DEBUG] Term marked as updating:`, {
                            key,
                            newTerm,
                            updating: true,
                            updating_from: oldTerm
                        });
                    }
                }
                return {
                    ...store,
                    termEntries: newTermEntries,
                    highlightedEntries: newHighlightedEntries
                };
            });
            
            // STEP 4: Backend operations - delete old, add new
            console.log(`[PersistentTerms] Step 4: Syncing with backend (delete old, add new)`);
            
            // Delete the old term from persistent storage
            await this.deleteTermByText(oldTerm, updatedClaimsText);
            
            // Add the new term to persistent storage (will trigger ML analysis for child terms)
            const result = await this.addManualTerm(newTerm, updatedClaimsText);
            
            // Log success
            console.log(`✅ Term modification complete: "${oldTerm}" → "${newTerm}" (${totalReplacements} replacements)`);
            if (childTerms.length > 0) {
                console.log(`✅ Child terms replaced: ${childTerms.join(', ')}`);
            }
            
            // Debug: Check if updating flag should be cleared
            console.log(`[PersistentTerms DEBUG] Modification complete, checking if updating flag will be cleared`);
            console.log(`[PersistentTerms DEBUG] Result being returned:`, {
                hasTextChanges: !!result.text_changes,
                hasTermCounts: !!result.term_counts,
                willApplyBackendResult: true
            });
            
            // Return result with additional info about the modification
            return {
                ...result,
                text_changes: {
                    old_term: oldTerm,
                    new_term: newTerm,
                    replacements_made: totalReplacements,
                    child_terms_replaced: childTerms,
                    method: 'replace_delete_add'
                },
                updated_claims_text: updatedClaimsText
            };
            
        } catch (error) {
            console.error(`[PersistentTerms DEBUG] ❌ Error modifying term "${oldTerm}":`, error);
            console.error(`[PersistentTerms DEBUG] Error details:`, {
                message: error.message,
                stack: error.stack,
                operationId
            });
            
            // Try to rollback by refreshing terms without the modification
            console.log(`[PersistentTerms] Attempting to rollback modification...`);
            try {
                // Get current claims text from store
                let currentClaimsText = '';
                const unsubscribe = this.claimsStore.subscribe(state => {
                    currentClaimsText = state.content || '';
                });
                unsubscribe();
                
                // Just refresh terms to get back to consistent state
                // The claims text may already be modified, but at least terms will be consistent
                if (currentClaimsText) {
                    await this.backendService.request('/analyze-claims', {
                        method: 'POST',
                        body: JSON.stringify({
                            text: currentClaimsText,
                            document_id: this.documentId,
                            force_heavy: true
                        })
                    });
                }
            } catch (rollbackError) {
                console.error(`❌ Rollback failed:`, rollbackError);
            }
            
            throw error;
        } finally {
            // Clear loading state
            console.log(`[PersistentTerms DEBUG] Finally block: clearing operation loading for ${operationId}`);
            this.termsStore.setOperationLoading(operationId, false);
            console.log(`[PersistentTerms DEBUG] Operation loading cleared`);
        }
    }
    // REMOVED: createOptimisticModifyResult method is no longer needed with the REPLACE approach
    // The complex position prediction logic has been replaced with simple text replacement
    
    /**
     * Update the claims editor with new text content
     * @param {string} newText - New claims text content
     */
    updateClaimsEditor(newText) {
        console.log('[PersistentTerms] Updating claims editor with modified text');
        
        // Update the claims store
        this.claimsStore.update(store => ({
            ...store,
            content: newText
        }));
        
        // If claims editor exists, update it directly
        if (window.claimsEditor && window.claimsEditor.state && window.claimsEditor.dispatch) {
            try {
                const currentDoc = window.claimsEditor.state.doc;
                const transaction = window.claimsEditor.state.tr.replace(
                    0, 
                    currentDoc.length, 
                    newText
                );
                window.claimsEditor.dispatch(transaction);
                console.log('[PersistentTerms] Claims editor updated successfully');
                
                // Force highlighting refresh after text update
                setTimeout(() => {
                    if (window.claimsEditor && window.claimsEditor.update) {
                        window.claimsEditor.update([]);
                        console.log('[PersistentTerms] Forced highlighting refresh after text update');
                    }
                }, 100);
            } catch (error) {
                console.warn('[PersistentTerms] Error updating claims editor directly:', error);
            }
        } else {
            console.log('[PersistentTerms] Claims editor not available, store updated only');
        }
    }
    
    
    /**
     * Get the current document ID for this session
     */
    getDocumentId() {
        return this.documentId;
    }
}

/**
 * Get the global document ID for session persistence
 * This can be used by other services to ensure consistent document ID
 */
export function getGlobalDocumentId() {
    return globalDocumentId;
}